import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Icon, Typography } from '@qtm/react2';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';

function renderPackageName(entry, darkMode) {
  const label = entry.name?.trim() || 'Unnamed access package';
  if (!entry.link) {
    return (
      <Typography variant="body-2" classes={darkMode ? 'text-bluegrey-200' : 'text-bluegrey-700'}>
        {label}
      </Typography>
    );
  }
  return (
    <Typography variant="body-2" classes={darkMode ? 'text-bluegrey-100' : 'text-bluegrey-800'}>
      <a
        href={entry.link}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: darkMode ? '#60a5fa' : '#1d4ed8',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        {label}
        <Icon icon="open_in_new" size="xsmall" classes="pl-xs" />
      </a>
    </Typography>
  );
}

export default function BmnIntegrationPage() {
  const { getToken } = useContext(AuthContext);
  const { darkMode } = useTheme();

  const PAGE_SIZE_CHOICES = useMemo(
    () => [
      { label: '10', value: '10' },
      { label: '25', value: '25' },
      { label: 'All', value: 'all' },
    ],
    [],
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState('10');
  const [currentPage, setCurrentPage] = useState(0);

  const containerStyle = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: 24,
      backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
      minHeight: '100%',
    }),
    [darkMode],
  );

  const cardStyle = useMemo(
    () => ({
      borderRadius: 16,
      padding: 24,
      backgroundColor: darkMode ? '#111827' : '#ffffff',
      boxShadow: darkMode
        ? '0 16px 32px rgba(15, 23, 42, 0.35)'
        : '0 16px 32px rgba(15, 23, 42, 0.12)',
      border: darkMode ? '1px solid rgba(148,163,184,0.25)' : '1px solid rgba(15,23,42,0.08)',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }),
    [darkMode],
  );

  const tableWrapperStyle = useMemo(
    () => ({
      width: '100%',
      overflowX: 'auto',
      borderRadius: 12,
      border: darkMode ? '1px solid rgba(148,163,184,0.35)' : '1px solid rgba(15,23,42,0.08)',
      backgroundColor: darkMode ? '#0b1120' : '#ffffff',
      boxShadow: darkMode
        ? '0 14px 32px rgba(15, 23, 42, 0.45)'
        : '0 12px 28px rgba(15, 23, 42, 0.12)',
    }),
    [darkMode],
  );

  const tableStyle = useMemo(
    () => ({
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      color: darkMode ? '#e2e8f0' : '#111827',
      fontSize: 14,
    }),
    [darkMode],
  );

  const headerCellStyle = useMemo(
    () => ({
      textAlign: 'left',
      padding: '14px 16px',
      backgroundColor: darkMode ? '#1f2937' : '#f6f8fa',
      color: darkMode ? '#e5e7eb' : '#111827',
      borderBottom: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }),
    [darkMode],
  );

  const nameCellStyle = useMemo(
    () => ({
      padding: '16px',
      verticalAlign: 'top',
      borderBottom: darkMode ? '1px solid #243046' : '1px solid #e5e7eb',
      backgroundColor: darkMode ? 'rgba(15,23,42,0.65)' : '#ffffff',
      width: '50%',
      minWidth: '240px',
      fontWeight: 600,
      color: darkMode ? '#f8fafc' : '#111827',
    }),
    [darkMode],
  );

  const packagesCellStyle = useMemo(
    () => ({
      padding: '16px 20px',
      borderBottom: darkMode ? '1px solid #243046' : '1px solid #e5e7eb',
      backgroundColor: darkMode ? 'rgba(15,23,42,0.45)' : '#ffffff',
      width: '50%',
      minWidth: '240px',
    }),
    [darkMode],
  );

  const loadCatalog = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Unable to acquire an authentication token.');
        }
        const response = await fetch('/api/bmns', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `Failed to load BMN catalog (${response.status}).`);
        }
        const body = await response.json();
        if (!Array.isArray(body)) {
          throw new Error('Unexpected BMN payload format.');
        }
        setEntries(
          body.map((item) => ({
            catalog_name: typeof item?.catalog_name === 'string' ? item.catalog_name : '',
            access_packages_data: Array.isArray(item?.access_packages_data)
              ? item.access_packages_data.map((pkg) => ({
                  name: typeof pkg?.name === 'string' ? pkg.name : '',
                  link: typeof pkg?.link === 'string' ? pkg.link : '',
                  is_hidden: Boolean(pkg?.is_hidden),
                }))
              : [],
          })),
        );
        setCurrentPage(0);
      } catch (err) {
        console.error('BmnIntegrationPage: failed to load catalog entries', err);
        setEntries([]);
        setError(err?.message || 'Unable to load BMN catalog.');
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [getToken],
  );

  useEffect(() => {
    loadCatalog(false);
  }, [loadCatalog]);

  const filteredEntries = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return entries;
    }
    return entries.filter((entry) => entry.catalog_name.toLowerCase().includes(term));
  }, [entries, query]);

  const paginatedEntries = useMemo(() => {
    if (pageSize === 'all') {
      return filteredEntries;
    }
    const size = Number(pageSize);
    const start = currentPage * size;
    return filteredEntries.slice(start, start + size);
  }, [currentPage, filteredEntries, pageSize]);

  useEffect(() => {
    if (pageSize === 'all') {
      if (currentPage !== 0) {
        setCurrentPage(0);
      }
      return;
    }
    const size = Number(pageSize);
    if (!size) {
      return;
    }
    const maxPage = Math.max(0, Math.ceil(filteredEntries.length / size) - 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, filteredEntries.length, pageSize]);

  const totalPages = useMemo(() => {
    if (!filteredEntries.length) {
      return 0;
    }
    if (pageSize === 'all') {
      return 1;
    }
    const size = Number(pageSize);
    return size ? Math.max(1, Math.ceil(filteredEntries.length / size)) : 1;
  }, [filteredEntries.length, pageSize]);

  const startEntryIndex = useMemo(() => {
    if (!filteredEntries.length) {
      return 0;
    }
    if (pageSize === 'all') {
      return 1;
    }
    return currentPage * Number(pageSize) + 1;
  }, [currentPage, filteredEntries.length, pageSize]);

  const endEntryIndex = useMemo(() => {
    if (!filteredEntries.length) {
      return 0;
    }
    if (pageSize === 'all') {
      return filteredEntries.length;
    }
    const size = Number(pageSize);
    return Math.min(filteredEntries.length, (currentPage + 1) * size);
  }, [currentPage, filteredEntries.length, pageSize]);

  const buildTableRows = useCallback(() => {
    if (!paginatedEntries.length) {
      return [];
    }
    return paginatedEntries.map((entry, entryIndex) => {
      const packages = entry.access_packages_data.length
        ? entry.access_packages_data
        : [
            {
              name: 'No access packages available',
              link: '',
              is_hidden: false,
              __placeholder: true,
            },
          ];

      return (
        <tr key={`catalog-${entryIndex}`}>
          <td style={nameCellStyle}>{entry.catalog_name?.trim() || 'Unnamed catalog'}</td>
          <td style={packagesCellStyle}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {packages.map((pkg, pkgIndex) =>
                pkg.__placeholder ? (
                  <Typography
                    key={`catalog-${entryIndex}-pkg-${pkgIndex}`}
                    variant="body-2"
                    classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-600'}
                  >
                    {pkg.name}
                  </Typography>
                ) : (
                  <div
                    key={`catalog-${entryIndex}-pkg-${pkgIndex}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    {renderPackageName(pkg, darkMode)}
                  </div>
                ),
              )}
            </div>
          </td>
        </tr>
      );
    });
  }, [darkMode, nameCellStyle, packagesCellStyle, paginatedEntries]);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Typography variant="h4">BMN Integration</Typography>
            <Typography
              variant="body-2"
              classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-600'}
            >
              Explore the BMN catalog and request the expected access package.
            </Typography>
          </div>
          <Button
            variant="ghost"
            size="small"
            onClickEvent={() => loadCatalog(true)}
            disabled={refreshing}
          >
            <Icon icon="refresh" />
            <span style={{ marginLeft: 6 }}>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
          </Button>
        </div>

        {!!error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            maxWidth: 360,
          }}
        >
          <input
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setCurrentPage(0);
            }}
            placeholder="Filter by catalog name"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: darkMode
                ? '1px solid rgba(148,163,184,0.35)'
                : '1px solid rgba(15,23,42,0.12)',
              backgroundColor: darkMode ? '#0b1120' : '#ffffff',
              color: darkMode ? '#e2e8f0' : '#0f172a',
              fontSize: 14,
            }}
          />
        </div>

        {loading ? (
          <Typography
            variant="body-2"
            classes={darkMode ? 'text-bluegrey-200' : 'text-bluegrey-600'}
          >
            Loading BMN catalog…
          </Typography>
        ) : filteredEntries.length === 0 ? (
          <Typography
            variant="body-2"
            classes={darkMode ? 'text-bluegrey-200' : 'text-bluegrey-600'}
          >
            No matching BMN catalog entries available.
          </Typography>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
                <label htmlFor="bmn-page-size" style={{ fontSize: 14 }}>
                  Items per page
                </label>
                <select
                  id="bmn-page-size"
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(event.target.value);
                    setCurrentPage(0);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: darkMode
                      ? '1px solid rgba(148,163,184,0.35)'
                      : '1px solid rgba(15,23,42,0.12)',
                    backgroundColor: darkMode ? '#0b1120' : '#ffffff',
                    color: darkMode ? '#e2e8f0' : '#0f172a',
                    fontSize: 14,
                  }}
                >
                  {PAGE_SIZE_CHOICES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <Typography
                variant="body-2"
                classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-600'}
              >
                Showing {startEntryIndex}-{endEntryIndex} of {filteredEntries.length}
              </Typography>

              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  variant="ghost"
                  size="small"
                  onClickEvent={() => setCurrentPage((value) => Math.max(0, value - 1))}
                  disabled={pageSize === 'all' || currentPage === 0}
                >
                  <Icon icon="chevron_left" />
                  Prev
                </Button>
                <Button
                  variant="ghost"
                  size="small"
                  onClickEvent={() =>
                    setCurrentPage((value) =>
                      pageSize === 'all' ? value : Math.min(value + 1, totalPages - 1),
                    )
                  }
                  disabled={pageSize === 'all' || currentPage >= totalPages - 1}
                >
                  Next
                  <Icon icon="chevron_right" classes="pl-2xs" />
                </Button>
              </div>
            </div>
            <div style={tableWrapperStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Catalog name</th>
                    <th style={headerCellStyle}>Access package</th>
                  </tr>
                </thead>
                <tbody>{buildTableRows()}</tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
